import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download, Check, ChevronRight, ArrowRight, Ruler, Weight, Timer, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const STAT_ICONS = { 0: Ruler, 1: Ruler, 2: Weight, 3: Shield };

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const ProductDetailPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allProducts, setAllProducts] = useState([]);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setLoading(true);
    const fetchProduct = async () => {
      try {
        const { data } = await axios.get(`${API}/api/products/${slug}`);
        setProduct(data);
      } catch (e) {
        console.error('Error fetching product:', e);
      } finally {
        setLoading(false);
      }
    };
    const fetchAllProducts = async () => {
      try {
        const { data } = await axios.get(`${API}/api/products`);
        setAllProducts(data);
      } catch (e) {
        console.error('Error fetching all products:', e);
      }
    };
    fetchProduct();
    fetchAllProducts();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0e1c3f]">
        <div className="h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-2 border-[#0078b7] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 py-24 text-center">
          <h1 className="font-display font-bold text-3xl text-[#0e1c3f] mb-4">Produit non trouvé</h1>
          <Button asChild className="bg-[#0e1c3f] text-white rounded-sm">
            <Link to="/produits">Voir tous les produits</Link>
          </Button>
        </div>
      </div>
    );
  }

  const specs = {
    stats: product.stats || [],
    highlights: product.highlights || []
  };
  const otherProducts = allProducts.filter(p => p.slug !== slug);
  const parallaxOffset = scrollY * 0.3;

  return (
    <div data-testid="product-detail-page">
      {/* Hero - Fullscreen immersive */}
      <section className="relative h-screen overflow-hidden">
        {/* Parallax image */}
        <div
          className="absolute inset-0 w-full h-[120%]"
          style={{ transform: `translateY(-${parallaxOffset}px)` }}
        >
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0e1c3f] via-[#0e1c3f]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0e1c3f] via-transparent to-[#0e1c3f]/30" />

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-end pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/produits"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors w-fit"
            data-testid="back-to-products"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Nos produits</span>
          </Link>

          <h1 className="font-display font-bold text-3xl sm:text-5xl lg:text-6xl xl:text-8xl text-white uppercase tracking-tight leading-none mb-4 sm:mb-6">
            {product.name}
          </h1>

          <p className="text-white/70 text-base sm:text-lg lg:text-xl max-w-2xl leading-relaxed mb-6 sm:mb-10">
            {product.short_description}
          </p>

          {/* Stats bar */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-4 sm:gap-6 lg:gap-10">
            {specs.stats.map((stat, i) => (
              <div key={i} className="group" data-testid={`stat-${i}`}>
                <p className="font-display font-bold text-3xl sm:text-4xl text-[#0078b7] group-hover:text-[#ff65c3] transition-colors">
                  {stat.value}
                </p>
                <p className="text-white/50 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <div className="w-5 h-8 border-2 border-white/30 rounded-full flex justify-center pt-1.5">
            <div className="w-1 h-2 bg-white/60 rounded-full" />
          </div>
        </div>
      </section>

      {/* Description + Highlights */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
            {/* Left - Description */}
            <AnimatedSection>
              <p className="text-[#ff65c3] font-bold tracking-[0.2em] uppercase text-sm mb-4">Description</p>
              <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0e1c3f] uppercase tracking-tight mb-8">
                {product.name}
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-10">
                {product.full_description || product.short_description}
              </p>

              {/* Features checklist */}
              {product.features && product.features.length > 0 && (
                <div className="space-y-4">
                  {product.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-4 group">
                      <div className="w-10 h-10 bg-[#0078b7]/10 group-hover:bg-[#0078b7] rounded-sm flex items-center justify-center flex-shrink-0 transition-colors">
                        <Check className="h-5 w-5 text-[#0078b7] group-hover:text-white transition-colors" />
                      </div>
                      <span className="text-slate-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* PDF Download */}
              {product.pdf_url && (
                <div className="mt-10">
                  <a
                    href={product.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 px-6 py-3 bg-[#0e1c3f] text-white font-medium text-sm hover:bg-[#0078b7] transition-colors rounded-sm"
                    data-testid="pdf-download-btn"
                  >
                    <Download className="h-5 w-5" />
                    Télécharger la fiche technique
                  </a>
                </div>
              )}
            </AnimatedSection>

            {/* Right - Highlights cards */}
            <div className="space-y-6">
              {specs.highlights.map((h, i) => (
                <AnimatedSection key={i} delay={i * 150}>
                  <div className="group p-5 sm:p-8 border border-slate-200 hover:border-[#0078b7] transition-all hover:-translate-y-1 hover:shadow-lg">
                    <div className="flex items-start gap-5">
                      <div className="w-12 h-12 bg-[#0e1c3f] group-hover:bg-[#0078b7] rounded-sm flex items-center justify-center flex-shrink-0 transition-colors">
                        <span className="text-white font-heading font-black text-lg">{String(i + 1).padStart(2, '0')}</span>
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-xl text-[#0e1c3f] mb-2 group-hover:text-[#0078b7] transition-colors">
                          {h.title}
                        </h3>
                        <p className="text-slate-600 leading-relaxed">{h.text}</p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specs + Domains */}
      {(product.technical_specs?.length > 0 || product.domains?.length > 0) && (
        <section className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Technical Specs */}
              {product.technical_specs?.length > 0 && (
                <AnimatedSection>
                  <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-4">Fiche technique</p>
                  <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0e1c3f] uppercase tracking-tight mb-8">
                    Caractéristiques techniques
                  </h2>
                  <div className="space-y-0">
                    {product.technical_specs.map((spec, i) => (
                      <div key={i} className={`flex items-start gap-4 p-4 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} border-b border-slate-200`} data-testid={`tech-spec-${i}`}>
                        <span className="text-slate-500 font-medium text-sm min-w-[140px] sm:min-w-[180px] flex-shrink-0">{spec.label}</span>
                        <span className="text-[#0e1c3f] font-semibold">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </AnimatedSection>
              )}

              {/* Domains */}
              {product.domains?.length > 0 && (
                <AnimatedSection delay={150}>
                  <p className="text-[#ff65c3] font-bold tracking-[0.2em] uppercase text-sm mb-4">Applications</p>
                  <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0e1c3f] uppercase tracking-tight mb-8">
                    Domaines d'utilisation
                  </h2>
                  <div className="space-y-3">
                    {product.domains.map((domain, i) => (
                      <div key={i} className="flex items-center gap-4 group" data-testid={`domain-${i}`}>
                        <div className="w-10 h-10 bg-[#0e1c3f] group-hover:bg-[#0078b7] rounded-sm flex items-center justify-center flex-shrink-0 transition-colors">
                          <ChevronRight className="h-5 w-5 text-white" />
                        </div>
                        <span className="text-slate-700 font-medium">{domain}</span>
                      </div>
                    ))}
                  </div>
                </AnimatedSection>
              )}
            </div>
          </div>
        </section>
      )}

      {/* CTA Band */}
      <AnimatedSection>
        <section className="bg-[#0e1c3f] relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#0078b7] rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-[#ff65c3] rounded-full blur-3xl" />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 sm:gap-8">
              <div>
                <h2 className="font-display font-bold text-xl sm:text-2xl lg:text-3xl text-white uppercase mb-2">
                  Un projet avec des {product.name.toLowerCase()} ?
                </h2>
                <p className="text-slate-400 text-sm sm:text-base">Notre bureau d'études vous accompagne de la conception à la livraison.</p>
              </div>
              <div className="flex flex-wrap gap-3 sm:gap-4 flex-shrink-0">
                {product.pdf_url && (
                  <a
                    href={product.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white hover:bg-white/10 transition-colors font-medium"
                    data-testid="download-pdf-btn"
                  >
                    <Download className="h-4 w-4" />
                    Fiche technique
                  </a>
                )}
                <Button asChild size="lg" className="bg-[#0078b7] hover:bg-[#0078b7]/90 text-white font-medium px-8 rounded-sm">
                  <Link to="/contact">
                    Demander un devis
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Other Products */}
      {otherProducts.length > 0 && (
        <section className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection>
              <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-8 sm:mb-12 gap-4">
                <div>
                  <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-3">Découvrez aussi</p>
                  <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0e1c3f] uppercase tracking-tight">
                    Nos autres produits
                  </h2>
                </div>
                <Button asChild variant="outline" className="border-[#0e1c3f] text-[#0e1c3f] hover:bg-[#0e1c3f]/5 rounded-sm hidden sm:flex">
                  <Link to="/produits">
                    Tous les produits
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {otherProducts.slice(0, 3).map((p, i) => (
                <AnimatedSection key={p.id} delay={i * 100}>
                  <Link
                    to={`/produits/${p.slug}`}
                    className="group block bg-white overflow-hidden border border-slate-200 hover:border-[#0078b7] transition-all hover:-translate-y-1 hover:shadow-xl"
                    data-testid={`other-product-${p.slug}`}
                  >
                    <div className="aspect-[4/3] overflow-hidden relative">
                      <img
                        src={p.image_url}
                        alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0e1c3f]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading font-bold text-lg text-[#0e1c3f] mb-2 group-hover:text-[#0078b7] transition-colors">
                        {p.name}
                      </h3>
                      <p className="text-slate-500 text-sm line-clamp-2">{p.short_description}</p>
                      <div className="mt-4 flex items-center gap-2 text-[#0078b7] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                        Découvrir <ArrowRight className="h-4 w-4" />
                      </div>
                    </div>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetailPage;
